import * as R from 'remeda';
import { FilterOperator, operators } from '../mql/operators';
import {
  FilterColDef,
  FilterContext,
  isFilterContextSimple,
  ValidatedFilterItem,
  ValidatedGridFilterItem,
} from './types';

export type FiltersState = Record<string, ValidatedFilterItem>;
type ExistingFilterState = FiltersState | null;

const getColumn = (
  filterItem: ValidatedGridFilterItem,
  columns: FilterColDef[]
): FilterColDef =>
  columns.find(col => col.field === filterItem.field) ?? {
    field: filterItem.field,
    operators: operators.filter(op => op.value === filterItem.operator),
  };

const isValidStringOrNumber = (value: unknown) =>
  typeof value === 'string' || typeof value === 'boolean';

const mergeNormalizers = {
  multiSelect: <EV, IV>(existingValue: EV, incomingValue: IV) => {
    const existingArray = Array.isArray(existingValue) ? existingValue : [];
    const incomingArray = Array.isArray(incomingValue) ? incomingValue : [];
    return R.unique([...existingArray, ...incomingArray]);
  },
  string: (_: unknown, incomingValue: unknown) => {
    return isValidStringOrNumber(incomingValue) ? incomingValue : null;
  },
  number: (_: unknown, incomingValue: unknown) => {
    return isValidStringOrNumber(incomingValue) ? incomingValue : null;
  },
  date: (_: unknown, incomingValue: unknown) => incomingValue,
} as const;

const getOperator = (operator: string, operatorlist?: FilterOperator[]) =>
  operatorlist?.find(op => op.value === operator);

// if for some reason we receive a filter query that has the same operator multiple times (e.g. 2-3 'isAnyOf') we normalize them
// by merging the values together, UNLESS the column allows multiple operator instances
const mergeSameColumns = (
  incomingFilters: ValidatedGridFilterItem[],
  columns: Array<FilterColDef>
): ValidatedGridFilterItem[] => {
  return incomingFilters.reduce<ValidatedGridFilterItem[]>((acc, item) => {
    const { operator, value, field } = item;

    const operatorType = Array.isArray(value)
      ? 'multiSelect'
      : getOperator(
          operator,
          operators.map(op => op)
        )?.type;

    // remove column if operator is unknown
    if (!operatorType) return acc;

    // Check if this column allows multiple instances of the same operator
    const column = columns.find(col => col.field === field);
    const allowMultipleInstances =
      (column &&
        column.allowedMultipleOperatorInstances &&
        column.allowedMultipleOperatorInstances.some(op => op === operator)) ??
      false;

    const existingFilter = acc.find(
      el => el.operator === operator && el.field === field
    );

    // If column allows multiple instances, don't merge - keep them separate
    if (existingFilter && !allowMultipleInstances) {
      const mergedValues = mergeNormalizers[operatorType](
        existingFilter.value,
        value
      );

      return mergedValues
        ? acc.map(el =>
            el.operator === operator && el.field === field
              ? { ...item, value: mergedValues }
              : el
          )
        : acc;
    }

    return acc.concat(item);
  }, []);
};

export const getInitialFilter = (
  existingFilterItems: ValidatedGridFilterItem[],
  columns: Array<FilterColDef>
): ExistingFilterState => {
  const filterItemsWithMergedColumns = mergeSameColumns(
    existingFilterItems,
    columns
  );

  return filterItemsWithMergedColumns.reduce<ExistingFilterState>(
    (acc, gridFilterItem) => {
      const column = getColumn(gridFilterItem, columns);
      const operator = getOperator(gridFilterItem.operator, column?.operators);

      const newFilter: FilterContext | null = operator
        ? {
            operator,
            value: gridFilterItem.value,
          }
        : null;

      // this means that the url has some unexpected value
      if (!newFilter) return acc;

      const existingColumn = acc?.[gridFilterItem.field];

      if (existingColumn) {
        // simpleFilter only exists if we re-iterate over the column for the first time which is the moment we move simple filter into advancedFilters
        const existingFilters = isFilterContextSimple(
          existingColumn.filterContext
        )
          ? [existingColumn.filterContext]
          : existingColumn.filterContext;

        return {
          ...acc,
          [gridFilterItem.field]: {
            column,
            filterContext: [...existingFilters, newFilter],
            isValid: gridFilterItem.isValid,
          },
        };
      }

      const prev = acc ?? {};

      return {
        ...prev,
        [gridFilterItem.field]: {
          column,
          filterContext: newFilter,
          isValid: gridFilterItem.isValid,
        },
      };
    },
    null
  );
};
